// The sidebar container: a black rail that collapses to an icon-only column
// (docs/projet/interface/README.md) instead of disappearing — the toggle lives
// in the rail itself. Mobile keeps Dust's sheet behavior unchanged.

import { INPUT_BAR_COMPACT_SURFACE_CLASSES } from "@app/components/assistant/conversation/input_bar/inputBarCompactStyles";
import { AgentSidebarMenu } from "@app/components/assistant/conversation/SidebarMenu";
import type { SidebarNavigation } from "@app/components/navigation/config";
import { NavigationSidebar } from "@app/components/navigation/NavigationSidebar";
import { SidebarContext } from "@app/components/sparkle/SidebarContext";
import {
  LARGEUR_BARRE_INTEGREE,
  modeIntegre,
} from "@app/lib/compta/mode_integre";
import { useUser } from "@app/lib/swr/user";
import type { SubscriptionType } from "@app/types/plan";
import type { WorkspaceType } from "@app/types/user";
import {
  Button,
  cn,
  Menu01,
  MessageChatSquare,
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@dust-tt/sparkle";
import { useLingui } from "@lingui/react/macro";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import type React from "react";
import { useContext } from "react";

const MOBILE_NAV_MENU_BUTTON_CLASSES = cn(
  INPUT_BAR_COMPACT_SURFACE_CLASSES,
  "transition-none",
  "hover:border-transparent hover:bg-hover hover:backdrop-blur-none",
  "active:border-transparent active:bg-primary-300 active:backdrop-blur-none"
);

interface NavigationProps {
  hideSidebar: boolean;
  owner: WorkspaceType;
  subscription: SubscriptionType;
  // Kept for call-site compatibility: the compta sidebar renders no page
  // children (the conversations list is gone).
  navChildren?: React.ReactNode;
  subNavigation?: SidebarNavigation[] | null;
  isNavigationBarOpen: boolean;
  setNavigationBarOpen: (isOpen: boolean) => void;
  isFullScreen: boolean;
  isMobile: boolean;
}

export function Navigation({
  hideSidebar,
  owner,
  subscription,
  subNavigation,
  isNavigationBarOpen,
  isMobile,
}: NavigationProps) {
  const { t } = useLingui();
  const { sidebarOpen, setSidebarOpen } = useContext(SidebarContext);

  const { user } = useUser();

  // Sous-plan 10 : dans l'iframe de la page Assistant de l'app, la barre laterale est la liste des
  // conversations de Dust (comme dans l'extension), sans rail compta ni menu utilisateur : l'app
  // fournit sa propre navigation autour de l'iframe. Une visite directe de /chat/ est inchangee.
  const integre = modeIntegre();

  if (hideSidebar) {
    return null;
  }

  const barre = (mobile: boolean) =>
    integre ? (
      <div className="flex flex-1 flex-col overflow-hidden p-1">
        <AgentSidebarMenu
          owner={owner}
          hideActions
          hideInAppBanner
          masquerPods
          masquerRecherche
          navigationApp
        />
      </div>
    ) : (
      <NavigationSidebar
        owner={owner}
        subscription={subscription}
        subNavigation={subNavigation}
        user={user}
        isMobile={mobile}
      />
    );
  // Le bouton de repli vit dans le rail : sans rail, la colonne reste ouverte.
  const ouverte = integre || isNavigationBarOpen;

  return (
    <div className={cn("flex shrink-0 overflow-x-hidden")}>
      {isMobile ? (
        <Sheet open={sidebarOpen} onOpenChange={setSidebarOpen}>
          <div className="fixed left-0 top-[var(--banner-height)] z-40 flex shrink-0 items-center px-2 pt-2">
            <SheetTrigger asChild>
              <Button
                variant="ghost"
                // Lot B : dans l'iframe de l'app, ce bouton ouvre les conversations ; l'app garde le sien.
                icon={integre ? MessageChatSquare : Menu01}
                className={MOBILE_NAV_MENU_BUTTON_CLASSES}
                onClick={() => setSidebarOpen(true)}
                aria-label={
                  integre ? "Ouvrir les conversations" : t`Open navigation`
                }
              />
            </SheetTrigger>
          </div>
          <SheetContent
            side="left"
            className="flex w-full max-w-xs flex-1 bg-app-background"
          >
            <SheetHeader className="bg-app-background p-0" hideButton={true}>
              <VisuallyHidden>
                <SheetTitle className="hidden" />
              </VisuallyHidden>
            </SheetHeader>
            {barre(true)}
          </SheetContent>
        </Sheet>
      ) : (
        <div
          className={cn(
            "transition-width flex flex-none flex-col overflow-hidden duration-150 ease-out",
            ouverte ? (integre ? LARGEUR_BARRE_INTEGREE : "w-64") : "w-16"
          )}
        >
          <div
            className={cn(
              "inset-y-0 z-0 flex flex-1 flex-col",
              ouverte ? (integre ? LARGEUR_BARRE_INTEGREE : "w-64") : "w-16"
            )}
          >
            {barre(false)}
          </div>
        </div>
      )}
    </div>
  );
}
